const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
//
// PERFORMANCE FIX: the previous version called percentageSeen() (which reads
// getBoundingClientRect() and offsetHeight, both of which force a synchronous
// layout) immediately during setup for every zoom-trigger element, each call
// interleaved with a style.setProperty() write for the previous element. That
// read/write/read/write pattern across elements forces the browser to
// recalculate layout once per element during initial page load, confirmed via
// Chrome DevTools Performance trace (~55ms of Layout self-time attributed to
// this function during the homepage load).
//
// Fix: batch all the reads first, then all the writes (read phase, then write
// phase), and defer the whole initial calculation into requestAnimationFrame
// so it runs after the browser's own paint work instead of blocking it
// synchronously during script evaluation. The scroll handler is also switched
// from a plain throttle to rAF-aligned updates for the same reason.
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;
  const visibility = new Map();

  animationTriggerElements.forEach((element) => {
    visibility.set(element, false);
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        visibility.set(element, entry.isIntersecting);
      });
    });
    observer.observe(element);
  });

  // Initial calculation: batch all reads first, then all writes, deferred to
  // requestAnimationFrame so it doesn't compete with first paint.
  requestAnimationFrame(() => {
    const values = animationTriggerElements.map((element) => percentageSeen(element));
    animationTriggerElements.forEach((element, index) => {
      element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * values[index]);
    });
  });

  let scrollTicking = false;
  window.addEventListener(
    'scroll',
    () => {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(() => {
        const values = animationTriggerElements.map((element) =>
          visibility.get(element) ? percentageSeen(element) : null
        );
        animationTriggerElements.forEach((element, index) => {
          if (values[index] === null) return;
          element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * values[index]);
        });
        scrollTicking = false;
      });
    },
    { passive: true }
  );
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}